import { FC, StrictMode } from 'react'
import { MediaStateProvider, NavStateProvider, SnackProvider } from '@reapit/elements'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { routes } from '@crm/routes'
import { LegacyIframesVisibilityProvider } from '@reapit-sales/legacy-crm'
import {
  BrowserTabsTrackerProvider,
  CRMTrackingProvider,
  ErrorBoundary,
  isSafariBrowser,
} from '@reapit-sales/utilities'
import { DisableAppForSafari } from '@crm/components/error-pages/DisableAppForSafari'

import '@reapit/elements/dist/index.css'

const queryClient = new QueryClient()
const router = createBrowserRouter(routes, {
  future: {
    v7_prependBasename: true,
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  } as any,
})

/**
 * The main App component for the CRM application.
 * It sets up providers for state management, data fetching, error handling, and routing.
 */
const App: FC = () => {
  const isSafariBrowserDetected: boolean = isSafariBrowser()

  if (isSafariBrowserDetected) {
    return <DisableAppForSafari />
  }

  return (
    <StrictMode>
      <BrowserTabsTrackerProvider>
        <CRMTrackingProvider>
          <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
              <SnackProvider>
                <NavStateProvider>
                  <MediaStateProvider>
                    <LegacyIframesVisibilityProvider>
                      <RouterProvider router={router} />
                    </LegacyIframesVisibilityProvider>
                  </MediaStateProvider>
                </NavStateProvider>
              </SnackProvider>
            </ErrorBoundary>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </CRMTrackingProvider>
      </BrowserTabsTrackerProvider>
    </StrictMode>
  )
}

export default App
